import { useEffect, useRef } from "react";
import { Instagram, Film, Clapperboard, Quote } from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import { useLanguage } from "@/i18n/LanguageContext";
import AboutCarousel from "./AboutCarousel";
// Tła sekcji "About" — desktopowe oraz mobilne. Wyświetlane z opacity 55-60% pod overlayem.
import filmPolskiBg from "@/assets/film-polski-bg.jpg";
import filmPolskiBgMobile from "@/assets/film-polski-bg-mobile.jpg";

// Custom FPV drone icon (quad-style top-down)
const FpvDroneIcon = ({ className, strokeWidth = 1.6 }: { className?: string; strokeWidth?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {/* arms (X frame) */}
    <line x1="4" y1="4" x2="20" y2="20" />
    <line x1="20" y1="4" x2="4" y2="20" />
    {/* propellers */}
    <circle cx="4" cy="4" r="3" />
    <circle cx="20" cy="4" r="3" />
    <circle cx="4" cy="20" r="3" />
    <circle cx="20" cy="20" r="3" />
    {/* central body w/ camera dot */}
    <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
  </svg>
);

const AboutSection = () => {
  const { t } = useLanguage();
  const imgRef = useRef<HTMLImageElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!imgRef.current || !sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const y = -rect.top * 0.2;
      imgRef.current.style.transform = `translateY(${y}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section id="about" className="pt-2 md:pt-4 pb-6 md:pb-10">
      <div ref={sectionRef} className="relative py-16 md:py-24 overflow-hidden">
        {/* Background photo with parallax — only behind the about copy + links */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Mobile: pre-rotated 90deg so it covers correctly without transform conflicts */}
          <img
            src={filmPolskiBgMobile}
            alt=""
            loading="lazy"
            decoding="async"
            className="md:hidden absolute inset-0 w-full h-full object-cover object-center opacity-55"
          />
          {/* Desktop: original landscape — cover full block height */}
          <img
            ref={imgRef}
            src={filmPolskiBg}
            alt=""
            loading="lazy"
            decoding="async"
            className="hidden md:block absolute inset-0 w-full h-full object-cover object-center opacity-60"
          />
          <div className="absolute inset-0 bg-background/40" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6">
        <ScrollReveal>
          <span className="block text-center text-accent text-xs font-mono tracking-widest uppercase">
            [ {t.about.label} // 02 ]
          </span>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <h2 className="mt-4 text-center text-4xl md:text-5xl font-bold text-foreground leading-tight glitch cursor-default uppercase lg:text-4xl whitespace-pre-line">
            {t.about.heading}
          </h2>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <p className="mt-8 text-lg md:text-xl text-foreground/80 leading-relaxed text-center whitespace-pre-line font-mono drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
            {t.about.paragraph1}
          </p>
        </ScrollReveal>

        <ScrollReveal delay={400}>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-md md:max-w-2xl mx-auto">
            {[
              {
                idx: "01",
                name: "VIMEO",
                handle: "betterpictures",
                href: "https://vimeo.com/betterpictures",
                Icon: Film,
              },
              {
                idx: "02",
                name: "INSTAGRAM",
                handle: "@wencek_michal",
                href: "https://instagram.com/wencek_michal",
                Icon: Instagram,
              },
              {
                idx: "03",
                name: "FILM POLSKI",
                handle: "credits",
                href: "https://filmpolski.pl/fp/index.php?osoba=11180978",
                Icon: Clapperboard,
              },
              {
                idx: "04",
                name: "AIRVUZ",
                handle: "wencek_michal",
                href: "https://www.airvuz.com/user/wencek_michal/videos",
                Icon: FpvDroneIcon,
              },
            ].map((item) => (
              <a
                key={item.idx}
                href={item.href}
                rel="noopener"
                className="group relative md:aspect-square flex flex-row md:flex-col items-center justify-center gap-2.5 md:gap-2 px-4 py-5 md:p-0 border border-foreground/15 bg-background/70 backdrop-blur-sm transition-all duration-300 hover:border-accent hover:bg-background hover:-translate-y-0.5 overflow-hidden"
              >
                <span className="hidden md:block absolute top-1 left-1.5 font-mono text-[8px] text-accent tracking-[0.2em]">
                  {item.idx}
                </span>
                <item.Icon
                  strokeWidth={1.25}
                  className="w-7 h-7 md:w-9 md:h-9 text-foreground transition-all duration-300 group-hover:text-accent group-hover:scale-110 shrink-0"
                />
                <span className="text-sm md:text-xs font-extrabold tracking-tight text-foreground min-w-0 leading-tight text-left md:text-center">
                  {item.name === "FILM POLSKI" ? (
                    <>
                      <span className="md:hidden whitespace-pre-line">{"FILM\nPOLSKI"}</span>
                      <span className="hidden md:inline">FILM POLSKI</span>
                    </>
                  ) : item.name === "INSTAGRAM" ? (
                    <>
                      <span className="md:hidden">INSTA</span>
                      <span className="hidden md:inline">INSTAGRAM</span>
                    </>
                  ) : (
                    item.name
                  )}
                </span>
              </a>
            ))}
          </div>
        </ScrollReveal>
        </div>
      </div>

      {/* Full-bleed horizontal scroll gallery */}
      <ScrollReveal delay={300}>
        <AboutCarousel />
      </ScrollReveal>
    </section>);

};

export default AboutSection;